import {
  useApplicationsServiceApplicationsApplicationsListKey,
  useApplicationsServiceApplicationsApplicationsRepositoriesListKey,
  useApplicationsServiceApplicationsApplicationsRetrieve,
  useApplicationsServiceApplicationsRemoveDestroy
} from '@organization/api/queries'
import {
  ApiError,
  ApplicationRepositoryDetail
} from '@organization/api/requests'
import { ApplicationTypes } from '@organization/features'
import { useApplicationIcon } from '@organization/hooks'
import {
  AppIconsNames,
  applicationTypes
} from '@organization/layout/Applications'
import { useInvalidateQueries, useSessionContext } from '@shared/hooks'
import { _Application } from '@shared/types/custom/_Application'
import { showApiErrors } from '@shared/utils'
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Flex,
  Tag,
  theme,
  Typography
} from 'antd'

import { Trash } from 'iconsax-react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStyle } from './styles'

const HIGHLIGHTS_ROCKET_APPLICATION = 'rocket'

type Props = {
  item: ApplicationTypes
  backgroundColor: string
  isDisabled?: boolean
  onSubmit?: (item: ApplicationRepositoryDetail) => void
}

const isApplication = (item: ApplicationTypes): item is _Application =>
  (item as _Application).instance_type !== undefined

export const ApplicationItem = ({
  item,
  backgroundColor,
  isDisabled = false,
  onSubmit
}: Props) => {
  const { user } = useSessionContext()
  const { token } = theme.useToken()
  const { modal } = App.useApp()
  const { t } = useTranslation()

  const itemType = isApplication(item) ? item?.instance_type?.type : item?.type

  const { icon: AppIcon } =
    applicationTypes[itemType ?? ''] ?? applicationTypes[AppIconsNames.Default]
  const applicationIcon = useApplicationIcon(itemType ?? '')

  const { styles, cx } = useStyle({
    backgroundColor: isDisabled ? token.colorFillContent : backgroundColor,
    organizationColor: applicationIcon,
    cursor: isDisabled ? 'not-allowed' : 'pointer'
  })
  const { invalidateQueries } = useInvalidateQueries()

  const { notification } = App.useApp()

  const navigate = useNavigate()

  const { data } = useApplicationsServiceApplicationsApplicationsRetrieve(
    {
      id: Number(item?.id)
    },
    [item?.id],
    {
      enabled: isApplication(item) && !isDisabled
    }
  )

  const { mutate, isPending } = useApplicationsServiceApplicationsRemoveDestroy(
    {
      onSuccess: async () => {
        invalidateQueries([
          useApplicationsServiceApplicationsApplicationsListKey,
          String(user?.organization?.id),
          useApplicationsServiceApplicationsApplicationsRepositoriesListKey,
          String(user?.organization?.id)
        ])

        notification.success({ message: t('application.deleted') })
      },
      onError: (e: ApiError) => showApiErrors(e)
    }
  )

  const onAppDelete = (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation()
    modal.confirm({
      title: `${t('modal.delete_title')} ${item?.name} ${t(
        'common.application'
      )}?`,
      okType: 'danger',
      cancelText: t('button.cancel'),
      okButtonProps: {
        loading: isPending
      },
      onOk: () => {
        mutate({
          appToken: String(data?.token)
        })
      }
    })
  }

  const redirectOrganization = (
    app: _Application | ApplicationRepositoryDetail
  ) => {
    if (isDisabled) return

    if (isApplication(app)) {
      if (itemType === 'link') {
        window.location.href = app?.settings?.url
        return
      }

      const baseUrl = app?.settings?.BASE_WEB_URL ?? `/apps/${itemType}`
      navigate(`${baseUrl}/${data?.token}/`)
    }
  }

  const onClick = (app: ApplicationTypes | ApplicationRepositoryDetail) => {
    if (isApplication(app)) {
      redirectOrganization(app)
    } else {
      onSubmit?.(app as ApplicationRepositoryDetail)
    }
  }

  const renderAppContent = () => (
    <Card
      aria-disabled={isDisabled}
      size="small"
      className={cx(styles.applicationItem, styles.cursor)}
      title={
        itemType?.startsWith(HIGHLIGHTS_ROCKET_APPLICATION) && (
          <Tag>{itemType}</Tag>
        )
      }
      extra={
        isApplication(item) ? (
          <Button
            type="link"
            danger
            icon={<Trash size="16" />}
            onClick={onAppDelete}
          />
        ) : (
          <span>&nbsp;</span>
        )
      }
      onClick={() => onClick(item)}
    >
      <Flex gap={8} vertical align="center">
        <Avatar
          size={64}
          className={cx(styles.avatar, applicationIcon)}
          src={<AppIcon />}
        />

        <Typography.Title level={5}>{item?.name}</Typography.Title>

        {isApplication(item) && item?.instance_type?.description}
      </Flex>
    </Card>
  )

  return (
    <Col xs={24} sm={12} md={12} lg={8} xl={6}>
      {renderAppContent()}
    </Col>
  )
}
